import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Plus, 
  Zap, 
  CheckCircle2, 
  ChevronRight 
} from 'lucide-react';
import { UserProfile, FinancialConfig } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile;
  config: FinancialConfig;
  materials: any[];
  completion: number;
  pendingTask: { label: string; action: () => void };
  onEditProfile: (step?: any) => void;
  onOpenMaterials: () => void;
}

export const SetupProgressModal: React.FC<Props> = ({ 
  isOpen, 
  onClose, 
  user, 
  config, 
  materials, 
  completion, 
  pendingTask,
  onEditProfile,
  onOpenMaterials
}) => {
  const pillars = [
    { id: 1, label: 'Identidade Pessoal', status: !!(user.ownerName && user.email), action: () => onEditProfile('profile') },
    { id: 2, label: 'Nicho de Atuação', status: !!(user.niche && user.profession), action: () => onEditProfile('business') },
    { id: 3, label: 'Marca e Identidade', status: !!(user.businessName && (user.logoUrl || user.logoBase64)), action: () => onEditProfile('business') },
    { id: 4, label: 'Sua Localização', status: !!(user.city && user.state && user.zipCode), action: () => onEditProfile('business') },
    { id: 5, label: 'Configuração Fiscal & PIX', status: !!(user.taxRegime && (user.pixKey || user.pixKeyType)), action: () => onEditProfile('business') },
    { id: 6, label: 'Salário Desejado', status: config.proLaboreGoal > 0, action: () => onEditProfile('financial') },
    { id: 7, label: 'Custos Fixos Mensais', status: config.monthlyFixedCost > 0, action: () => onEditProfile('financial') },
    { id: 8, label: 'Jornada de Trabalho', status: config.weeklyWorkHours > 0, action: () => onEditProfile('financial') },
    { id: 9, label: 'Centro de Custos (Inventário)', status: (materials?.length || 0) > 0, action: onOpenMaterials },
    { id: 10, label: 'Termos e Privacidade', status: !!user.lgpdConsent?.termsAccepted, action: () => onEditProfile('lgpd') },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center p-4 bg-zinc-900/80 backdrop-blur-md">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white w-full max-w-md rounded-[32px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-brand text-white">
              <div>
                <h3 className="text-lg font-black uppercase tracking-widest">Sua Jornada R-MAX</h3>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-widest">Os 10 Passos para um Negócio Seguro</p>
              </div>
              <button 
                onClick={onClose}
                className="bg-white/20 p-2 rounded-full hover:bg-white/30 transition-colors"
              >
                <Plus className="h-5 w-5 rotate-45" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {completion < 100 && pendingTask.label !== 'Tudo pronto para orçar! 🚀' && (
                <motion.button
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    pendingTask.action();
                    onClose();
                  }}
                  className="w-full bg-linear-to-r from-brand to-brand-dark p-5 rounded-[24px] shadow-xl shadow-brand/20 flex items-center justify-between group relative overflow-hidden"
                >
                  <div className="relative z-10 text-left">
                    <span className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-1 block">Próximo Passo Recomendado</span>
                    <h4 className="text-sm font-black text-white uppercase tracking-tight">{pendingTask.label}</h4>
                  </div>
                  <div className="bg-white/20 p-2.5 rounded-xl backdrop-blur-md relative z-10">
                    <Zap className="h-5 w-5 text-white animate-pulse" />
                  </div>
                  
                  <motion.div 
                    className="absolute inset-0 bg-linear-to-r from-transparent via-white/10 to-transparent"
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  />
                </motion.button>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between px-1">
                  <h5 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Sua Jornada Completa</h5>
                  <span className="text-[10px] font-bold text-brand">{completion}% concluído</span>
                </div>
                {pillars.map((pillar) => (
                  <button
                    key={pillar.id}
                    onClick={() => {
                      pillar.action();
                      onClose();
                    }}
                    className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all active:scale-[0.98] ${
                      pillar.status 
                        ? 'bg-emerald-50/50 border-emerald-100/50 text-emerald-900 shadow-none' 
                        : 'bg-white border-zinc-100 text-zinc-900 shadow-sm hover:border-brand/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        pillar.status ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-400'
                      }`}>
                        {pillar.status ? <CheckCircle2 className="h-5 w-5" /> : <span className="text-xs font-black">{pillar.id}</span>}
                      </div>
                      <span className={`text-xs font-bold ${pillar.status ? 'opacity-40 line-through' : ''}`}>
                        {pillar.label}
                      </span>
                    </div>
                    {!pillar.status && (
                      <ChevronRight className="h-4 w-4 text-brand opacity-50" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6 bg-zinc-50 border-t border-zinc-100">
              <button 
                onClick={onClose}
                className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 transition-colors shadow-xl"
              >
                Continuar no R-MAX
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
